/**
 * Custom element for dialog functionality triggered by URL hash
 * @class
 * @extends HTMLElement
 */
class AppDialog extends HTMLElement {
  /**
   * Called when the element is inserted into the DOM
   * Initializes dialog functionality and event listeners
   */
  connectedCallback() {
    this.trigger = this.dataset.trigger;
    this.dialog = this.querySelector('dialog');

    if (!this.dialog || !this.trigger) return;

    if (window.location.hash.includes(this.trigger)) {
      this.showDialog();
    }

    window.addEventListener('hashchange', this.onHashChange);

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-close]')) {
        this.closeDialog();
      }
    });

    // Open the dialog when the section is selected in the theme editor
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', (event) => {
        if (event.detail.sectionId === this.dataset.sectionId) {
          this.showDialog();
        }
      });

      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.detail.sectionId === this.dataset.sectionId) {
          this.closeDialog();
        }
      });
    }
  }

  /**
   * Called when the element is removed from the DOM
   * Cleans up event listeners to prevent memory leaks
   */
  disconnectedCallback() {
    window.removeEventListener('hashchange', this.onHashChange);
    this.removeEventListener('click', (event) => {
      if (event.target.closest('[data-close]')) {
        this.closeDialog();
      }
    });
  }

  /**
   * Event handler for hash changes in URL
   * Shows dialog when hash matches trigger
   */
  onHashChange = () => {
    if (window.location.hash.includes(this.trigger)) {
      this.showDialog();
    }
  };

  /**
   * Shows the dialog modal
   * Dispatches a custom event when dialog opens
   */
  showDialog = () => {
    try {
      if (this.dialog && !this.dialog.open) {
        this.dialog.showModal();

        // Dispatch a custom event when dialog opens (not currently used, but for best practices)
        this.dispatchEvent(
          new CustomEvent('dialog:open', {
            bubbles: true,
            detail: { id: this.trigger },
          })
        );
      }
    } catch (error) {
      console.error('Error showing dialog:', error);
    }
  };

  /**
   * Closes the dialog modal
   * Dispatches a custom event when dialog closes
   */
  closeDialog = () => {
    try {
      if (this.dialog && this.dialog.open) {
        this.dialog.close();
        const payload = { dialogId: this.trigger };
        publish('dialog:close', payload);
      }
    } catch (error) {
      console.error('Error closing dialog:', error);
    }
  };
}

customElements.define('app-dialog', AppDialog);
